import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { fetchPdfActions, openPdf as openPdfHelper, downloadPdf as downloadPdfHelper, sharePdf as sharePdfHelper } from '@/lib/pdfActions';
import { isIOS as isIOSPlatform } from '@/lib/platform';
import { Link } from 'react-router-dom';
import { FunnelSimple, ClipboardText, Plus, X, Check, Truck, Receipt, ArrowsClockwise, Link as LinkIcon, FilePdf, Eye, Printer, DownloadSimple, Trash, TrashSimple } from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  concept:        { label: 'Concept',       bg: '#F3F4F6', fg: '#374151', order: 1 },
  verzonden:      { label: 'Verzonden',     bg: '#DBEAFE', fg: '#1E40AF', order: 2 },
  bevestigd:      { label: 'Bevestigd',     bg: '#D1FAE5', fg: '#065F46', order: 3 },
  gewijzigd:      { label: 'Gewijzigd',     bg: '#FEF3C7', fg: '#92400E', order: 4 },
  geleverd:       { label: 'Geleverd',      bg: '#E0E7FF', fg: '#3730A3', order: 5 },
  gefactureerd:   { label: 'Gefactureerd',  bg: '#FCE7F3', fg: '#831843', order: 6 },
  geannuleerd:    { label: 'Geannuleerd',   bg: '#FEE2E2', fg: '#991B1B', order: 99 },
};

const fmt = (n) => `€ ${Number(n || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AankoopBestellingenPage() {
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLev, setFilterLev] = useState('');
  const [leveranciers, setLeveranciers] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [threeWeg, setThreeWeg] = useState(null);
  const [pdfPreviewId, setPdfPreviewId] = useState(null);

  const load = () => {
    API.get('/aankoopbestellingen').then(r => setItems(r.data || []));
    API.get('/leveranciers').then(r => setLeveranciers(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(x => {
    if (filterStatus && x.status !== filterStatus) return false;
    if (filterLev && x.leverancier_id !== filterLev && !(x.leverancier || '').toLowerCase().includes(filterLev.toLowerCase())) return false;
    return true;
  });

  const byStatus = items.reduce((acc, x) => {
    const s = x.status || 'concept';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const wijzigStatus = async (bid, newStatus) => {
    try {
      await API.post(`/aankoopbestellingen/${bid}/status`, { status: newStatus });
      load();
      if (drawer?.id === bid) setDrawer(d => ({ ...d, status: newStatus }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Status wijziging mislukt');
    }
  };

  const pdfUrl = (bid) => `${API_BASE}/api/aankoopbestellingen/${bid}/pdf`;

  const downloadPdf = async (bid) => {
    try {
      const actions = await fetchPdfActions({
        endpoint: `/aankoopbestellingen/${bid}/pdf`,
        label: `Aankoopbestelling-${bid}`,
        filename: `Aankoopbestelling-${bid}.pdf`,
      });
      // iOS: openen in viewer (gebruiker kan vandaar opslaan/delen); desktop: direct download
      if (isIOSPlatform()) openPdfHelper(actions); else downloadPdfHelper(actions);
    } catch (e) {
      alert('Download mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const printPdf = async (bid) => {
    try {
      const actions = await fetchPdfActions({
        endpoint: `/aankoopbestellingen/${bid}/pdf`,
        label: `Aankoopbestelling-${bid}`,
        filename: `Aankoopbestelling-${bid}.pdf`,
      });
      openPdfHelper(actions);
    } catch (e) {
      alert('Open mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sharePdfPO = async (bid) => {
    try {
      const actions = await fetchPdfActions({
        endpoint: `/aankoopbestellingen/${bid}/pdf`,
        label: `Aankoopbestelling-${bid}`,
        filename: `Aankoopbestelling-${bid}.pdf`,
      });
      await sharePdfHelper(actions, { onError: (err) => alert('Delen mislukt: ' + err.message) });
    } catch (e) {
      alert('Delen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const openDrawer = async (po) => {
    setDrawer(po);
    setThreeWeg(null);
  };

  const verwijder = async (bid, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Aankoopbestelling ${bid} verwijderen?`)) return;
    try {
      await API.delete(`/aankoopbestellingen/${bid}`);
      if (drawer?.id === bid) setDrawer(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const verwijderAlles = async () => {
    const totaal = items.length;
    if (totaal === 0) {
      alert('Geen aankoopbestellingen om te verwijderen.');
      return;
    }
    const behoudGefactureerd = window.confirm(
      `Wil je gefactureerde bestellingen BEHOUDEN?\n\n` +
      `• OK → enkel niet-gefactureerde bestellingen worden verwijderd (veilig).\n` +
      `• Annuleren → ALLES wordt verwijderd, ook gefactureerde (let op!).`
    );
    const label = behoudGefactureerd ? 'niet-gefactureerde bestellingen' : 'ALLE bestellingen (incl. gefactureerde)';
    if (!window.confirm(`Weet je zeker dat je ${label} wil verwijderen?\n\nDit kan NIET ongedaan gemaakt worden.`)) return;
    try {
      const r = await API.delete(`/aankoopbestellingen/alles?behoud_gefactureerd=${behoudGefactureerd}`);
      alert(`${r.data?.verwijderd ?? 0} aankoopbestelling(en) verwijderd.`);
      setDrawer(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk-verwijderen mislukt');
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="aankoopbestellingen-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0 max-w-full break-words">
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Aankoopbestellingen</h1>
          <p className="text-xs text-gray-500 mt-0.5">Overzicht van alle bestellingen bij leveranciers met status-tracking</p>
        </div>
        <div className="flex flex-wrap max-w-full items-center gap-2">
          <button
            className="btn btn-sm inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={verwijderAlles}
            disabled={items.length === 0}
            title="Alle aankoopbestellingen verwijderen (met optie om gefactureerde te behouden)"
            data-testid="delete-all-btn"
          >
            <TrashSimple size={14} /> Verwijder alles
          </button>
          <Link to="/aankopen?tab=bestellen" className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="new-po-btn">
            <Plus size={14} /> Nieuwe bestelling
          </Link>
        </div>
      </div>

      {/* Status-tiles */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-6">
        <button
          className={`p-3 border text-left transition-all ${!filterStatus ? 'border-[#002FA7] bg-[#F0F4FF]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          onClick={() => setFilterStatus('')}
          data-testid="filter-all"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Totaal</div>
          <div className="text-xl font-bold text-[#0A0A0A] mt-0.5">{items.length}</div>
        </button>
        {Object.entries(STATUS_META).sort((a,b) => a[1].order - b[1].order).map(([k, meta]) => (
          <button
            key={k}
            className={`p-3 border text-left transition-all ${filterStatus === k ? 'border-[#002FA7] bg-[#F0F4FF]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
            onClick={() => setFilterStatus(k === filterStatus ? '' : k)}
            data-testid={`filter-${k}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.fg }}>{meta.label}</div>
            <div className="text-xl font-bold text-[#0A0A0A] mt-0.5">{byStatus[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* Leverancier-filter */}
      <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-gray-50 border border-gray-200">
        <FunnelSimple size={14} className="text-gray-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Leverancier:</span>
        <select
          className="field-input text-xs max-w-xs"
          value={filterLev}
          onChange={e => setFilterLev(e.target.value)}
          data-testid="filter-lev"
        >
          <option value="">— Alle leveranciers —</option>
          {leveranciers.map(l => (
            <option key={l.id} value={l.id}>{l.naam}</option>
          ))}
        </select>
        {(filterStatus || filterLev) && (
          <button
            className="ml-auto text-xs text-[#002FA7] hover:underline font-semibold"
            onClick={() => { setFilterStatus(''); setFilterLev(''); }}
            data-testid="clear-filters"
          >
            Wis filters
          </button>
        )}
      </div>

      {/* Tabel */}
      <div className="bg-white border border-gray-200">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <ClipboardText size={36} weight="thin" className="mx-auto mb-2 opacity-40" />
            <div className="text-sm">Geen aankoopbestellingen gevonden</div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="po-table">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2.5 font-bold">Nummer</th>
                <th className="text-left px-4 py-2.5 font-bold">Datum</th>
                <th className="text-left px-4 py-2.5 font-bold">Leverancier</th>
                <th className="text-center px-4 py-2.5 font-bold">Status</th>
                <th className="text-center px-4 py-2.5 font-bold">Lijnen</th>
                <th className="text-right px-4 py-2.5 font-bold">Totaal</th>
                <th className="text-center px-4 py-2.5 font-bold">Gefactureerd</th>
                <th className="text-center px-4 py-2.5 font-bold w-[150px]">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(po => {
                const meta = STATUS_META[po.status] || STATUS_META.concept;
                return (
                  <tr
                    key={po.id}
                    className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                    onClick={() => openDrawer(po)}
                    data-testid={`po-row-${po.id}`}
                  >
                    <td className="px-4 py-2.5 font-mono font-bold text-[#002FA7]">{po.id}</td>
                    <td className="px-4 py-2.5 text-gray-600">{po.datum || '-'}</td>
                    <td className="px-4 py-2.5 font-medium">{po.leverancier || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-block text-[10px] font-bold uppercase px-2 py-0.5" style={{ background: meta.bg, color: meta.fg }}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{(po.lijnen || []).length}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900">{fmt(po.totaal)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {po.inkomend_factuur_id ? (
                        <span className="text-[10px] font-bold text-[#008A27] bg-green-50 px-1.5 py-0.5">✓ {po.inkomend_factuur_id}</span>
                      ) : (
                        <span className="text-[10px] text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          className="p-1.5 hover:bg-[#002FA7]/10 transition-colors"
                          title="Preview PDF"
                          onClick={() => setPdfPreviewId(po.id)}
                          data-testid={`preview-${po.id}`}
                        >
                          <Eye size={16} weight="bold" className="text-[#002FA7]" />
                        </button>
                        <button
                          className="p-1.5 hover:bg-red-50 transition-colors"
                          title="Download PDF"
                          onClick={() => downloadPdf(po.id)}
                          data-testid={`download-${po.id}`}
                        >
                          <FilePdf size={16} weight="bold" className="text-red-600" />
                        </button>
                        <button
                          className="p-1.5 hover:bg-gray-100 transition-colors"
                          title="Afdrukken"
                          onClick={() => printPdf(po.id)}
                          data-testid={`print-${po.id}`}
                        >
                          <Printer size={16} weight="bold" className="text-gray-700" />
                        </button>
                        <button
                          className="p-1.5 hover:bg-red-50 transition-colors"
                          title="Verwijderen"
                          onClick={(e) => verwijder(po.id, e)}
                          data-testid={`delete-${po.id}`}
                        >
                          <Trash size={16} weight="bold" className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {drawer && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex justify-end" onClick={() => setDrawer(null)} data-testid="po-drawer">
          <div
            className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between sticky top-0 z-10">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Aankoopbestelling</div>
                <h3 className="text-lg font-bold text-[#0A0A0A]">{drawer.id}</h3>
              </div>
              <button className="text-gray-400 hover:text-gray-700" onClick={() => setDrawer(null)} data-testid="drawer-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Kernvelden */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Leverancier</div>
                  <div className="text-gray-900 mt-0.5">{drawer.leverancier || '-'}</div>
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Datum</div>
                  <div className="text-gray-900 mt-0.5">{drawer.datum || '-'}</div>
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Totaal</div>
                  <div className="text-gray-900 mt-0.5 font-bold">{fmt(drawer.totaal)}</div>
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Aantal lijnen</div>
                  <div className="text-gray-900 mt-0.5">{(drawer.lijnen || []).length}</div>
                </div>
              </div>

              {/* Status-transities */}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Status wijzigen</div>
                <div className="flex flex-wrap gap-1.5">
                  {['concept','verzonden','bevestigd','gewijzigd','geleverd','gefactureerd','geannuleerd'].map(s => {
                    const meta = STATUS_META[s];
                    const active = drawer.status === s;
                    return (
                      <button
                        key={s}
                        className={`px-2.5 py-1 text-[10px] font-bold uppercase transition-all ${active ? 'ring-2 ring-[#002FA7]' : 'hover:opacity-80'}`}
                        style={{ background: meta.bg, color: meta.fg }}
                        onClick={() => wijzigStatus(drawer.id, s)}
                        data-testid={`drawer-status-${s}`}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Lijnen preview */}
              {drawer.lijnen && drawer.lijnen.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Bestelde artikels</div>
                  <table className="w-full text-xs border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left font-bold text-gray-600">Ref</th>
                        <th className="px-2 py-1 text-left font-bold text-gray-600">Omschrijving</th>
                        <th className="px-2 py-1 text-right font-bold text-gray-600">Aantal</th>
                        <th className="px-2 py-1 text-right font-bold text-gray-600">Prijs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.lijnen.map((l, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1 font-mono text-[#002FA7]">{l.referentie || '-'}</td>
                          <td className="px-2 py-1 text-gray-700">{l.omschr || l.omschrijving || '-'}</td>
                          <td className="px-2 py-1 text-right text-gray-600">{l.aantal}</td>
                          <td className="px-2 py-1 text-right text-gray-600">{fmt(l.prijs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Gefactureerd link */}
              {drawer.inkomend_factuur_id && (
                <div className="bg-green-50 border border-green-200 p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Check size={16} weight="bold" className="text-[#008A27]" />
                    <span className="font-bold text-green-900">Gefactureerd via</span>
                    <Link to={`/inkomende-documenten/${drawer.inkomend_factuur_id}`} className="text-[#002FA7] hover:underline font-mono font-bold">
                      {drawer.inkomend_factuur_id}
                    </Link>
                  </div>
                </div>
              )}

              {/* Acties */}
              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <button
                  className="btn btn-secondary btn-sm flex-1"
                  onClick={() => verwijder(drawer.id)}
                  data-testid="drawer-delete"
                >
                  Verwijderen
                </button>
                <Link to={`/aankopen?po=${drawer.id}`} className="btn btn-secondary btn-sm flex-1 text-center" data-testid="drawer-edit">
                  Bewerken
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center justify-center gap-1"
                  onClick={() => setPdfPreviewId(drawer.id)}
                  data-testid="drawer-preview"
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center justify-center gap-1"
                  onClick={() => downloadPdf(drawer.id)}
                  data-testid="drawer-download"
                >
                  <DownloadSimple size={14} /> PDF
                </button>
                <button
                  className="btn btn-primary btn-sm inline-flex items-center justify-center gap-1"
                  onClick={() => printPdf(drawer.id)}
                  data-testid="drawer-print"
                >
                  <Printer size={14} /> Afdrukken
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF preview modal */}
      {pdfPreviewId && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-6" onClick={() => setPdfPreviewId(null)} data-testid="pdf-preview-modal">
          <div className="bg-white w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FilePdf size={18} weight="bold" className="text-red-600" />
                <span className="font-bold text-gray-900">Aankoopbestelling PDF</span>
                <span className="font-mono text-xs text-[#002FA7]">{pdfPreviewId}</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => downloadPdf(pdfPreviewId)} data-testid="modal-download">
                  <DownloadSimple size={14} /> Download
                </button>
                <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={() => printPdf(pdfPreviewId)} data-testid="modal-print">
                  <Printer size={14} /> Afdrukken
                </button>
                <button className="text-gray-400 hover:text-gray-700 ml-2" onClick={() => setPdfPreviewId(null)} data-testid="modal-close">
                  <X size={22} />
                </button>
              </div>
            </div>
            <iframe
              src={pdfUrl(pdfPreviewId)}
              title="PDF Preview"
              className="flex-1 border-0"
              data-testid="pdf-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
